import { jsx as _jsx } from "react/jsx-runtime";
import { ErrorPage, Loader, useGetContractIframeUrlQuery, useGetCreditApplicationById } from '@applyonline/shared';
import { EContractStatus, EFinancingProgram, EWorksheetStatus } from '@if/core/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
const PROGRESSION_INTERVAL_STEP = 100 / 180;
const ContractWaitingLoader = ({ creditAppId, financingProgram, contractIsReceived }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('shared');
    const [shouldPollCreditApp, setShouldPollCreditApp] = useState(true);
    const [shouldPollContract, setShouldPollContract] = useState(false);
    const [progress, setProgress] = useState(0);
    const isB2c = financingProgram === EFinancingProgram.B2c;
    const pollInterval = isB2c ? 14000 : 15000;
    const [creditApp] = useGetCreditApplicationById(creditAppId, financingProgram, shouldPollCreditApp, pollInterval);
    const { data: iframeDto, failureCount } = useGetContractIframeUrlQuery(creditAppId, financingProgram, shouldPollContract);
    if (creditApp && (creditApp.worksheet?.status === EWorksheetStatus.Draft || contractIsReceived(creditApp))) {
        navigate(`/creditapplication/${creditAppId}`);
    }
    useEffect(() => {
        if (!isB2c)
            return;
        const interval = setInterval(() => {
            setProgress((p) => {
                if (p >= 100 - PROGRESSION_INTERVAL_STEP)
                    clearInterval(interval);
                const np = p + PROGRESSION_INTERVAL_STEP;
                if (np > 100)
                    return 100;
                return np;
            });
        }, 1000);
        const timeoutPollCreditApp = setTimeout(() => {
            setShouldPollCreditApp(false);
            clearInterval(interval);
        }, 60000);
        return () => {
            clearTimeout(timeoutPollCreditApp);
            clearInterval(interval);
        };
    }, [isB2c]);
    useEffect(() => {
        if (isB2c)
            return;
        const timeoutPollCreditApp = setTimeout(() => {
            setShouldPollCreditApp(false);
        }, 60000);
        return () => {
            clearTimeout(timeoutPollCreditApp);
        };
    }, [isB2c]);
    useEffect(() => {
        if (creditApp?.contract.status === EContractStatus.Ready) {
            setShouldPollCreditApp(false);
            if (isB2c) {
                setProgress((1 / 3) * 100); // 1/3
            }
            setShouldPollContract(true);
        }
    }, [creditApp?.contract.status, isB2c]);
    const urlsDto = useMemo(() => iframeDto?.urls?.find((x) => x.isForApplicant === true), [iframeDto?.urls]);
    useEffect(() => {
        if (urlsDto?.url) {
            const url = isB2c ? `${urlsDto.url}&lang=${i18n.language}` : urlsDto.url;
            window.location.href = url;
        }
    }, [navigate, urlsDto?.url, isB2c, i18n.language]);
    if (failureCount === 6 || (!shouldPollCreditApp && creditApp?.contract.status !== EContractStatus.Ready)) {
        return isB2c
            ? _jsx(ErrorPage, { description: t('contract.errorMessage'), backHref: `/creditapplication/${creditAppId}` })
            : _jsx(ErrorPage, { title: t('contract.errorMessage') });
    }
    return (_jsx(Loader, { title: t('contract.waiting'), description: t('contract.waitingDescription'), progression: isB2c ? progress : undefined }));
};
export default ContractWaitingLoader;
